import { MixedSchema } from "yup";
import { DEFAULT_ACCEPTED_ATTACHMENT_EXTENSIONS } from "../../../../config/constants";
import { fileTypeFromBuffer } from "file-type";
import path from "node:path";

const MAX_FILE_SIZE_IN_BYTES = 6000000;
export const UPLOAD_FILE_INVALID_EXTENSION = "UPLOAD_FILE_INVALID_EXTENSION";
export const PROFILE_PHOTO_INVALID_EXTENSION =
  "PROFILE_PHOTO_INVALID_EXTENSION";
export const UPLOAD_FILE_SIZE_EXCEEDED = "UPLOAD_FILE_SIZE_EXCEEDED";

export function attachmentExtensionValidation(
  this: MixedSchema,
  acceptedExtensions?: string[],
  errorMessage?: string
) {
  return this.test(
    "attachment-extension",
    errorMessage || UPLOAD_FILE_INVALID_EXTENSION,
    function validate(value) {
      const file = value as File;

      return isExtensionValid(
        file,
        acceptedExtensions || DEFAULT_ACCEPTED_ATTACHMENT_EXTENSIONS
      );
    }
  );
}

export function attachmentSizeValidation(
  this: MixedSchema,
  maxSize?: number,
  errorMessage?: string
) {
  return this.test(
    "attachment-size",
    errorMessage || UPLOAD_FILE_SIZE_EXCEEDED,
    function validate(value) {
      const file = value as File;
      return !(file && file?.size > (maxSize || MAX_FILE_SIZE_IN_BYTES));
    }
  );
}

const thereIsNoDotInFilename = (filename: string) =>
  filename.lastIndexOf(".") === -1;

const extensionIsNotSupported = (
  validExtensions: string[],
  fileExtension: string
) =>
  !validExtensions
    .map((ext) => ext.toLowerCase())
    .includes(fileExtension.toLowerCase());

const isExtensionFromFileBufferAllowed = async (
  file: File,
  validExtensions: string[]
) => {
  const realExtensionFromFileBuffer = await fileTypeFromBuffer(
    await file.arrayBuffer()
  );

  return validExtensions
    .map((ext) => ext.toLowerCase())
    .includes(`.${realExtensionFromFileBuffer?.ext}`.toLowerCase());
};

async function isExtensionValid(
  file: File,
  validExtensions: string[]
): Promise<boolean> {
  // because when a file is not required, we don't want to show an extension error when not uploaded
  if (!file || !file.name) {
    return true;
  }

  if (thereIsNoDotInFilename(file.name)) {
    return false;
  }

  const fileExtension = path.extname(file.name).toLowerCase();

  if (extensionIsNotSupported(validExtensions, fileExtension)) {
    return false;
  }

  return isExtensionFromFileBufferAllowed(file, validExtensions);
}
